import React, { useEffect, useState } from "react";
import { Clapperboard, Loader2, Play, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/residentApi";
import { showApi } from "./showKit";
import ShowcasePlayer from "./ShowcasePlayer";
import VoicePicker from "./VoicePicker";
import MediaVoices from "./MediaVoices";
import PublicLinks from "./PublicLinks";

const mins = (s) => Math.round(s.scenes.reduce((a, x) => a + x.say.split(" ").length, 0) / 150 + s.scenes.length * 0.05);

export default function ShowcaseLauncher() {
  const [show, setShow] = useState(null);
  const [play, setPlay] = useState(null);
  const load = () => showApi.get().then(setShow).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!show?.preparing) return undefined; const h = setInterval(load, 8000); return () => clearInterval(h); }, [show?.preparing]);
  if (!show) return <div className="flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading the showcase…</div>;
  const prepare = () => showApi.prepare().then(() => { toast.success("Preparing narration in the background"); load(); }).catch((e) => toast.error(errMsg(e)));
  const total = show.segments.reduce((a, s) => a + mins(s), 0);
  const acts = [[1, "Act One · Platform Capabilities"], [2, "Act Two · Resident Impact Story"]];
  return (
    <div className="space-y-5 max-w-4xl" data-testid="showcase-launcher">
      <div className="relative overflow-hidden border border-nex-gold/30 bg-gradient-to-br from-[#1a1405] to-[#05070d] p-6">
        <Clapperboard className="absolute -right-6 -bottom-6 h-40 w-40 text-nex-gold/10" />
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-nex-gold">Platform Showcase · Super Admin</div>
        <h2 className="font-serif text-3xl text-white mt-2">The Exchange Alliance, in motion</h2>
        <p className="text-sm text-nex-muted mt-2">12 narrated segments · about {total} minutes · voice: {show.voice} · captions on · live platform numbers</p>
        <button type="button" onClick={() => setPlay(show.segments.map((_, i) => i))} data-testid="showcase-play-all" className="mt-5 inline-flex items-center gap-2 bg-nex-gold text-black font-semibold px-5 py-3 hover:brightness-110"><Play className="h-4 w-4" /> Play full presentation</button>
        <div className="mt-4 text-xs text-nex-muted flex flex-wrap items-center gap-3" data-testid="showcase-prep">
          <span>Narration written {show.expanded}/{show.total}{show.preparing ? ` · recording ${show.recorded}/${show.total}` : ""}</span>
          <button type="button" onClick={prepare} disabled={show.preparing} data-testid="showcase-prepare" className="inline-flex items-center gap-1 border border-nex-gold/40 text-nex-gold px-2 py-1 disabled:opacity-50">
            {show.preparing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}{show.preparing ? "Preparing…" : "Prepare narration"}</button>
          <span>Prepare once before a meeting so playback never waits.</span>
        </div>
      </div>
      <VoicePicker show={show} onChanged={load} />
      <MediaVoices />
      {acts.map(([a, label]) => (
        <section key={a} className="space-y-2">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-nex-gold">{label}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {show.segments.map((s, i) => s.act === a && (
              <button key={s.n} type="button" onClick={() => setPlay([i])} data-testid={`showcase-seg-${s.n}`} className="group flex items-center gap-3 border border-white/10 bg-white/[0.02] p-3 text-left hover:border-nex-gold/50 transition-colors">
                <span className="font-serif text-2xl text-nex-gold/70 w-9">{String(s.n).padStart(2, "0")}</span>
                <span className="flex-1"><span className="block text-sm text-nex-silver font-semibold">{s.title}</span><span className="text-[11px] text-nex-muted">{s.kicker.split("· ")[1]} · ~{mins(s)} min</span></span>
                <Play className="h-4 w-4 text-nex-muted group-hover:text-nex-gold" />
              </button>
            ))}
          </div>
        </section>
      ))}
      <PublicLinks segments={show.segments} />
      {play && <ShowcasePlayer show={show} segIdx={play} onExit={() => setPlay(null)} />}
    </div>
  );
}
